/* eslint-disable no-console */
"use client";

import { useCallback, useRef, useState, useEffect } from "react";

interface UseCopyToClipboardOptions {
  /** Milliseconds before `copied` flips back to false. Default 2000. */
  copiedDuration?: number | undefined;
  onCopy?: (() => void) | undefined;
  onCopyError?: ((error: Error) => void) | undefined;
}

interface UseCopyToClipboardReturn {
  copied: boolean;
  /**
   * Writes `value` to the clipboard and flips `copied` to true for
   * `copiedDuration` ms. Calls `event.stopPropagation()` when an event
   * is passed so the copy click doesn't bubble into surrounding row /
   * card click handlers.
   */
  copy: (event?: { stopPropagation?: () => void }) => Promise<void>;
}

/**
 * Shared clipboard-write + "copied" feedback state for any UI surface
 * that exposes a copy button. Owned by `packages/ui` so `Copyable`,
 * `CodeBlockCopyButton`, and any future copy affordance share the same
 * behaviour without duplicating the `useState` + `setTimeout` dance.
 */
export function useCopyToClipboard(
  value: string,
  options: UseCopyToClipboardOptions = {},
): UseCopyToClipboardReturn {
  const { copiedDuration = 2000, onCopy, onCopyError } = options;
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const copy = useCallback<UseCopyToClipboardReturn["copy"]>(
    async (event) => {
      event?.stopPropagation?.();
      try {
        await navigator.clipboard.writeText(value);
        setCopied(true);
        onCopy?.();
        if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
        timeoutRef.current = setTimeout(() => setCopied(false), copiedDuration);
      } catch (error) {
        console.error("Failed to copy:", error);
        onCopyError?.(error as Error);
      }
    },
    [value, copiedDuration, onCopy, onCopyError],
  );

  return { copied, copy };
}
